import { groupBy } from 'lodash-es'
import type { optionType } from './dictionary.ts'
import { whoTypesDictionary } from './education.ts'
import type {
    DocumentWithAuthor,
    DocumentWithPublicity,
    DocumentWithSoftDelete,
    DocumentWithTimestamps,
    Timestamp
} from './firebase.ts'
import type { OrgId, UserId } from './ids.ts'
import { StartType } from './Job.ts'
import { MentoringCategoriesMapped, MentoringCategory } from './Mentoring.ts'
import type { NotificationEmail } from './Notifications.ts'
import type { OrgMeta } from './Org.ts'
import type { RelatedItems } from './RelatedItems.ts'

// Týždeň Dobrovoľníctva
export enum ProjectType {
    VOLUNTEERING_WEEK = 'volunteering_week'
}

export interface FinishProjectCooperationFormData {
    message: string
}

export type ProjectId = string

export type Project = DocumentWithTimestamps &
    DocumentWithSoftDelete &
    DocumentWithAuthor &
    DocumentWithPublicity & {
        id: ProjectId
        slug?: string
        memberIds: UserId[]
        orgId: OrgId | null
        orgMeta: OrgMeta
        relatedItems?: RelatedItems
        title?: string
        image?: string
        desc?: string
        how: string[]
        duration: string[]
        maturity: string[]
        who: string[]
        whoGroupEnabled?: boolean
        whoGroupType?: string
        whoGroupSize?: string
        when?: string
        requiresProfessionalExperience?: boolean
        professionalExperienceDetails?: string
        disabilities?: ProjectDisability[]
        hasQualifiedPersonForDisabilities?: boolean
        unpublishAt?: Timestamp // When to unpublish the project. Set when created.
        /**
         * @deprecated Use when instead
         */
        startDate?: Timestamp | string
        /**
         * @deprecated Use when instead
         */
        startType?: string
        about?: string
        progress?: string
        intensityValue?: number
        intensityUnit?: string
        vision?: string
        benefits?: string
        impact?: string
        topics?: string[]
        expertise: string[]
        wanted: string[]
        area?: string
        geoRegion?: string
        geoCity?: string
        notifiableMembers: NotificationEmail[]
        type?: ProjectType | undefined | null
        maxVolunteerCount?: number | null
        receivedVolunteerRequestsCount?: number | string
        volunteeringWeekAutoPublish?: boolean
        organizationVolunteerOnboardingConfirmedAt?: Timestamp
        organizationVolunteerOnboardingConfirmedById?: UserId
        // Date properties for Search sync
        startDateAt?: Timestamp
        endDateAt?: Timestamp
        startDayAt?: number
        // Location for geo search
        location?: {
            lat: number
            lng: number
        }
    }

export type ProjectFormData = Partial<Project>

export enum ProjectCategoryWho {
    studentSs = 'secondary_school_student',
    studentVs = 'university_student',
    mladyProfesional = 'young_professional',
    profesional = 'professional'
}

export enum ProjectAudienceOption {
    individual15To18 = 'individual_15_18',
    individual18Plus = 'individual_18_plus',
    group = 'group'
}

export enum ProjectDisability {
    VISUAL = 'visual', // so zrakovým znevýhodnením
    HEARING = 'hearing', // so sluchovým znevýhodnením
    MOBILITY = 'mobility', // s pohybovým obmedzením
    COMMUNICATION = 'communication', // s narušenou komunikačnou schopnosťou
    SOCIAL = 'social', // zo sociálne znevýhodneného prostredia
    INTELLECTUAL = 'intellectual', // s mentálnym znevýhodnením
    MENTAL = 'mental' // s duševným ochorením
}

export const ProjectCategoryWhoNominativ: { [key in ProjectCategoryWho]: string } = {
    [ProjectCategoryWho.studentSs]: whoTypesDictionary.student_ss,
    [ProjectCategoryWho.studentVs]: whoTypesDictionary.student_vs,
    [ProjectCategoryWho.mladyProfesional]: whoTypesDictionary.mlady_profesional,
    [ProjectCategoryWho.profesional]: whoTypesDictionary.profesional
}

const PROJECT_AUDIENCE_ORDER: ProjectAudienceOption[] = [
    ProjectAudienceOption.individual15To18,
    ProjectAudienceOption.individual18Plus,
    ProjectAudienceOption.group
]

const PROJECT_AUDIENCE_18_PLUS_IDS: ProjectCategoryWho[] = [
    ProjectCategoryWho.studentVs,
    ProjectCategoryWho.mladyProfesional,
    ProjectCategoryWho.profesional
]

const PROJECT_AUDIENCE_18_PLUS_ID_SET = new Set<string>(PROJECT_AUDIENCE_18_PLUS_IDS)

export const ProjectAudienceOptions: optionType[] = [
    {
        id: ProjectAudienceOption.individual15To18,
        value: ProjectAudienceOption.individual15To18,
        label: 'Jednotlivec 15 až 18'
    },
    {
        id: ProjectAudienceOption.individual18Plus,
        value: ProjectAudienceOption.individual18Plus,
        label: 'Jednotlivec 18 a viac'
    },
    {
        id: ProjectAudienceOption.group,
        value: ProjectAudienceOption.group,
        label: 'Skupina'
    }
]

function isProjectAudienceOption(value: string): value is ProjectAudienceOption {
    return PROJECT_AUDIENCE_ORDER.includes(value as ProjectAudienceOption)
}

function sortProjectAudienceOptions(options: Set<ProjectAudienceOption>): ProjectAudienceOption[] {
    return PROJECT_AUDIENCE_ORDER.filter((option) => options.has(option))
}

function stringifyTypesenseArray(values: string[]): string {
    return `[${values.map((value) => `\`${value}\``).join(',')}]`
}

export function normalizeProjectAudienceSelection(selection: readonly string[] = []): ProjectAudienceOption[] {
    const normalized = new Set<ProjectAudienceOption>()

    selection.forEach((value) => {
        if (isProjectAudienceOption(value)) {
            normalized.add(value)
            return
        }

        if (value === ProjectCategoryWho.studentSs) {
            normalized.add(ProjectAudienceOption.individual15To18)
            return
        }

        if (PROJECT_AUDIENCE_18_PLUS_ID_SET.has(value)) {
            normalized.add(ProjectAudienceOption.individual18Plus)
        }
    })

    return sortProjectAudienceOptions(normalized)
}

export function projectAudienceSelectionToProjectWho(selection: readonly string[] = []): {
    who: ProjectCategoryWho[]
    whoGroupEnabled: boolean
} {
    const normalized = normalizeProjectAudienceSelection(selection)
    const who = new Set<ProjectCategoryWho>()

    if (normalized.includes(ProjectAudienceOption.individual15To18)) {
        who.add(ProjectCategoryWho.studentSs)
    }

    if (normalized.includes(ProjectAudienceOption.individual18Plus)) {
        PROJECT_AUDIENCE_18_PLUS_IDS.forEach((id) => who.add(id))
    }

    return {
        who: [...who],
        whoGroupEnabled: normalized.includes(ProjectAudienceOption.group)
    }
}

export function projectAudienceSelectionFromProject(project: {
    who?: string[]
    whoGroupEnabled?: boolean
}): ProjectAudienceOption[] {
    const normalized = new Set<ProjectAudienceOption>(normalizeProjectAudienceSelection(project.who ?? []))

    if (project.whoGroupEnabled) {
        normalized.add(ProjectAudienceOption.group)
    }

    return sortProjectAudienceOptions(normalized)
}

export function projectAudienceSelectionToTypesenseFilter(selection: readonly string[] = []): string {
    const { who, whoGroupEnabled } = projectAudienceSelectionToProjectWho(selection)
    const clauses: string[] = []

    if (who.length > 0) {
        clauses.push(`who:${stringifyTypesenseArray(who)}`)
    }

    if (whoGroupEnabled) {
        clauses.push('whoGroupEnabled:=true')
    }

    if (clauses.length === 0) {
        return ''
    }

    return clauses.length > 1 ? `(${clauses.join(' || ')})` : clauses[0] || ''
}

export function projectAudienceLabelsFromSelection(selection: readonly string[] = []): string[] {
    const normalized = normalizeProjectAudienceSelection(selection)
    const labelMap = new Map(ProjectAudienceOptions.map((option) => [option.id as ProjectAudienceOption, option.label]))

    return normalized.map((option) => labelMap.get(option)).filter((label): label is string => Boolean(label))
}

export function projectAudienceLabelsFromProject(project: { who?: string[]; whoGroupEnabled?: boolean }): string[] {
    return projectAudienceLabelsFromSelection(projectAudienceSelectionFromProject(project))
}

const excludedWho = [
    'teacher',
    'public_institution',
    'ngo_school',
    'organization_growing_company',
    'secondary_school_project'
]

export const ProjectCategoryWhoData = (MentoringCategoriesMapped[MentoringCategory.Who] ?? [])
    .map((item) => {
        return {
            id: item.id,
            value: item.value,
            label: item.label + (item.hint !== '' ? ' ' + item.hint : ''),
            labelNominativ: ProjectCategoryWhoNominativ[item.id as ProjectCategoryWho]
        }
    })
    .filter(({ id }) => !excludedWho.includes(id))

export const intensityUnits: optionType[] = [
    {
        id: 'h-d',
        value: 'h-d',
        label: 'hodín / denne'
    },
    {
        id: 'h-w',
        value: 'h-w',
        label: 'hodín / týždenne'
    },
    { id: 'h-m', value: 'h-m', label: 'hodín / mesačne' }
]

export const projectStartTypesList: optionType[] = [
    { id: StartType.ASAP, value: StartType.ASAP, label: 'Ihneď' },
    { id: StartType.MONTH, value: StartType.MONTH, label: 'Vybrať mesiac' },
    { id: StartType.DATE, value: StartType.DATE, label: 'Presný dátum' }
]

export const intensityUnitsMap = groupBy(intensityUnits, 'value')
